import {
	BlogHomeCustomization,
	BlogPostCustomization,
	CustomPageType,
	FooterCustomization,
	HeaderCustomization,
} from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { MastheadPortalId } from '../../../../models/AdminModels';
import {
	invalidateHostedBlogPagePreview,
	invalidateSchedulerPreviewCustomPageStyleQueries,
	invalidateSurveyPreviewQuery,
	useTemplateDesignerPreviewUrlsQuery,
} from '../../../../queries';
import {
	invalidateGetBlogStylesByTypeQueries,
	useGetBlogStylesByTypeQuery,
} from '../../../../queries/CustomPageStyle/useGetBlogStylesByTypeQuery';
import { ConfirmationDialog, DefaultConfirmationOptions } from '../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { useAccountContext } from '../../../components/blogs/AccountContext';
import { BreadcrumbsBarWithNameProvider } from '../../../components/blogs/BreadcrumbsBarWithNameProvider';
import { Device, PreviewPane } from './components/PreviewPane';
import { SegmentedTabs } from './components/SegmentedTabs';
import { FooterDesignForm, FooterDesignFormRef } from './Forms/FooterDesignForm';
import { HeaderDesignForm, HeaderDesignFormRef } from './Forms/HeaderDesignForm';
import { HomeDesignForm, HomeDesignFormRef } from './Forms/HomeDesignForm';
import { PostDesignForm, PostDesignFormRef } from './Forms/PostDesignForm';
import { PreviewContext } from './PreviewContextTabs';
import { PresetSelector } from './PresetSelector';
import { StylePreset } from './presets';
import { styleSheet } from './styles';

export const TemplateDesignFormContainer: FC = () => {
	const { account, impersonationContext } = useAccountContext();

	const blogFeature = account?.features?.blogFeature;
	const isBlogFeatureEnabled = blogFeature?.enabled;
	const isDudaHosted = !!blogFeature?.dudaSiteId;
	const isLevitateBlogEnabled = !isDudaHosted && isBlogFeatureEnabled;

	const surveyFeature = account?.features?.surveys;
	const isSurveyFeatureEnabled = surveyFeature?.enabled ?? false;
	const schedulerFeature = account?.features?.meetingScheduler;
	const isSchedulerFeatureEnabled = schedulerFeature?.enabled ?? false;

	// Fetch preview URLs to determine if surveys/meetings actually exist
	const { data: previewUrls } = useTemplateDesignerPreviewUrlsQuery({
		impersonationContext,
		enabled: !!impersonationContext,
	});

	// Combine feature availability with actual content availability
	const isSurveyAvailable = isSurveyFeatureEnabled && (previewUrls?.surveyAvailable ?? false);
	const isMeetingAvailable = isSchedulerFeatureEnabled && (previewUrls?.meetingAvailable ?? false);

	const [isDirty, setIsDirty] = useState<boolean>(false); // data in form has been edited, not saved
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const [selectedPageType, setSelectedPageType] = useState<CustomPageType>(CustomPageType.Header);
	const [pendingTab, setPendingTab] = useState<CustomPageType | null>(null);
	const [currentPresetId, setCurrentPresetId] = useState<string>('custom');
	const [previewContext, setPreviewContext] = useState<PreviewContext>('blog');
	const [previewDevice, setPreviewDevice] = useState<Device>('desktop');

	const headerFormRef = useRef<HeaderDesignFormRef>(null);
	const homeFormRef = useRef<HomeDesignFormRef>(null);
	const postFormRef = useRef<PostDesignFormRef>(null);
	const footerFormRef = useRef<FooterDesignFormRef>(null);

	// Applying a preset intentionally dirties the form, which fires the form-dirty handler. That
	// handler resets the selection to 'custom', which would clobber the preset we just selected.
	// This flag lets the handler tell "preset just applied" apart from a genuine manual edit.
	const presetJustAppliedRef = useRef<boolean>(false);

	const { data, isSuccess } = useGetBlogStylesByTypeQuery({
		impersonationContext,
		type: selectedPageType,
		meta: {
			toastErrorMessage: 'Error loading custom blog styles',
		},
	});
	useEffect(() => {
		if (isSuccess) {
			setIsDirty(false);
		}
	}, [isSuccess]);

	const handleFormUpdateSuccess = () => {
		invalidateGetBlogStylesByTypeQueries(selectedPageType);
		invalidateHostedBlogPagePreview(selectedPageType);
		invalidateSurveyPreviewQuery();
		invalidateSchedulerPreviewCustomPageStyleQueries();
		setIsDirty(false);
		setLastSavedAt(new Date());
	};

	const handleTabChange = (value: CustomPageType) => {
		// A preset's skip-once guard must never outlive the tab it was applied on.
		presetJustAppliedRef.current = false;
		if (!isDirty) {
			setSelectedPageType(value);
		} else {
			setPendingTab(value);
		}
	};

	const handleDialogClose = (_: never, canceled: boolean) => {
		if (!canceled && pendingTab) {
			presetJustAppliedRef.current = false;
			setSelectedPageType(pendingTab);
			setIsDirty(false);
		}
		setPendingTab(null);
	};

	const handleFormDirty = useCallback(() => {
		setIsDirty(true);
		if (presetJustAppliedRef.current) {
			presetJustAppliedRef.current = false;
			return;
		}
		setCurrentPresetId('custom');
	}, []);

	const handlePresetSelect = useCallback((preset: StylePreset) => {
		setCurrentPresetId(preset.id);

		// Only arm the skip-once guard when a form that will actually fire onFormDirty is mounted.
		// On Blog Home/Post neither form is mounted, so nothing would consume the flag and it would
		// wrongly suppress the reset-to-custom on the next genuine manual edit.
		if (headerFormRef.current || footerFormRef.current) {
			presetJustAppliedRef.current = true;
		}

		// Apply preset to Header and Footer only (not blog-specific forms)
		if (headerFormRef.current) {
			headerFormRef.current.applyPreset(preset);
		}
		if (footerFormRef.current) {
			footerFormRef.current.applyPreset(preset);
		}

		setIsDirty(true);
	}, []);

	const handleSave = () => {
		const formRefsByTab: Partial<Record<CustomPageType, { submit?: () => void }>> = {
			[CustomPageType.Header]: headerFormRef.current,
			[CustomPageType.BlogHome]: homeFormRef.current,
			[CustomPageType.BlogPost]: postFormRef.current,
			[CustomPageType.Footer]: footerFormRef.current,
		};

		formRefsByTab[selectedPageType]?.submit?.();
	};

	const isHeaderTab = selectedPageType === CustomPageType.Header;
	const isHomeTab = selectedPageType === CustomPageType.BlogHome;
	const isPostTab = selectedPageType === CustomPageType.BlogPost;

	const tabOptions: ReadonlyArray<{ value: CustomPageType; label: string }> = [
		{ value: CustomPageType.Header, label: 'Header' },
		...(isLevitateBlogEnabled
			? [
					{ value: CustomPageType.BlogHome, label: 'Blog Home' },
					{ value: CustomPageType.BlogPost, label: 'Blog Post' },
				]
			: []),
		{ value: CustomPageType.Footer, label: 'Footer' },
	];

	const showPreviewContextTabs =
		selectedPageType === CustomPageType.Header || selectedPageType === CustomPageType.Footer;

	return (
		<div className='admin-blog-design-form-container'>
			{account ? (
				<>
					<MultiContainerHeader
						appBarHeader={<BreadcrumbsBarWithNameProvider account={account} currentLocation='Template Designer' />}
						appBarPortalDestinationId={MastheadPortalId}
					/>
					<div className={css(styleSheet.bodyGrid)}>
						<div className={css(styleSheet.leftColumn)}>
							<SegmentedTabs
								options={tabOptions}
								value={selectedPageType}
								onChange={handleTabChange}
								ariaLabel='Page type'
							/>
							<PresetSelector
								currentPresetId={currentPresetId}
								onPresetSelect={handlePresetSelect}
								disabled={false}
								hasUnsavedChanges={isDirty}
							/>
							{isHeaderTab && (
								<HeaderDesignForm
									ref={headerFormRef}
									customContent={data?.type === CustomPageType.Header ? (data as HeaderCustomization) : undefined}
									previewContext={previewContext}
									onFormDirty={handleFormDirty}
									onUpdateSuccess={handleFormUpdateSuccess}
									onSavingChange={setIsSaving}
								/>
							)}
							{isLevitateBlogEnabled && isHomeTab && (
								<HomeDesignForm
									ref={homeFormRef}
									customContent={data?.type === CustomPageType.BlogHome ? (data as BlogHomeCustomization) : undefined}
									onFormDirty={handleFormDirty}
									onUpdateSuccess={handleFormUpdateSuccess}
									onSavingChange={setIsSaving}
								/>
							)}
							{isLevitateBlogEnabled && isPostTab && (
								<PostDesignForm
									ref={postFormRef}
									customContent={data?.type === CustomPageType.BlogPost ? (data as BlogPostCustomization) : undefined}
									onFormDirty={handleFormDirty}
									onUpdateSuccess={handleFormUpdateSuccess}
									onSavingChange={setIsSaving}
								/>
							)}
							{selectedPageType === CustomPageType.Footer && (
								<FooterDesignForm
									ref={footerFormRef}
									customContent={data?.type === CustomPageType.Footer ? (data as FooterCustomization) : undefined}
									onFormDirty={handleFormDirty}
									onUpdateSuccess={handleFormUpdateSuccess}
									onSavingChange={setIsSaving}
								/>
							)}
						</div>
						<div className={css(styleSheet.rightColumn)}>
							<PreviewPane
								pageType={selectedPageType}
								previewContext={previewContext}
								onPreviewContextChange={setPreviewContext}
								showContextTabs={showPreviewContextTabs}
								device={previewDevice}
								onDeviceChange={setPreviewDevice}
								isDirty={isDirty}
								isSaving={isSaving}
								lastSavedAt={lastSavedAt}
								onSave={handleSave}
								blogFeatureEnabled={isLevitateBlogEnabled}
								blogAvailable={isLevitateBlogEnabled}
								surveyFeatureEnabled={isSurveyFeatureEnabled}
								surveyAvailable={isSurveyAvailable}
								meetingFeatureEnabled={isSchedulerFeatureEnabled}
								meetingAvailable={isMeetingAvailable}
							/>
						</div>
					</div>
				</>
			) : (
				<LoadingSpinner />
			)}
			{pendingTab && (
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{ isOpen: !!pendingTab, onRequestClose: handleDialogClose }}
					options={DefaultConfirmationOptions}
					title='Changing tabs without saving will clear pending edits'
				/>
			)}
		</div>
	);
};
